import React, { useState, useRef, useEffect } from 'react';
import { Shield, Loader2 } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Props {
  onVerified: () => void;
  onCancel: () => void;
}

export const TwoFAVerifyModal: React.FC<Props> = ({ onVerified, onCancel }) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => { inputRef.current?.focus(); }, []);

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const token = (await supabase.auth.getSession()).data.session?.access_token;
      const res = await fetch('/api/admin/2fa/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ code }),
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.error || 'Ungültiger Code');
      } else {
        onVerified();
      }
    } catch {
      setError('Verbindungsfehler');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: 'var(--background)',
    }}>
      <div style={{
        width: 400, background: 'var(--surface)', borderRadius: 'var(--radius-xl)',
        border: '1px solid var(--border)', padding: 40, boxShadow: 'var(--shadow-lg)',
      }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{
            width: 48, height: 48, background: 'linear-gradient(135deg, #3B82F6, #8B5CF6)',
            borderRadius: 12, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: 16,
          }}>
            <Shield size={22} color="#fff" />
          </div>
          <h1 style={{ marginBottom: 4 }}>Zwei-Faktor-Authentifizierung</h1>
          <p className="text-muted">Gib den 6-stelligen Code aus deiner Authenticator-App ein.</p>
        </div>

        <form onSubmit={handleVerify}>
          <div style={{ marginBottom: 24 }}>
            <input
              ref={inputRef}
              type="text"
              className="input"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="000000"
              maxLength={6}
              autoComplete="one-time-code"
              style={{ textAlign: 'center', fontSize: 24, letterSpacing: 8, fontWeight: 700 }}
            />
          </div>

          {error && (
            <div className="alert alert-danger" style={{ marginBottom: 16 }}>{error}</div>
          )}

          <button
            type="submit"
            className="btn btn-primary w-full"
            disabled={loading || code.length < 6}
            style={{ height: 42, fontSize: 14, marginBottom: 12 }}
          >
            {loading ? <Loader2 style={{ animation: 'spin 1s linear infinite' }} /> : 'Verifizieren'}
          </button>

          <button
            type="button"
            className="btn btn-ghost w-full"
            onClick={onCancel}
            style={{ fontSize: 13 }}
          >
            Abmelden
          </button>
        </form>

        <p className="text-muted" style={{ fontSize: 11, textAlign: 'center', marginTop: 16 }}>
          Recovery Code? Gib ihn statt des 6-stelligen Codes ein.
        </p>

        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    </div>
  );
};
